/* Imports */
// eslint-disable-next-line import/no-unresolved
import { defineStore } from 'pinia';
import { postSort } from '../helpers/postSort.js';
import { request } from '../helpers/request.js';

const defaultState = () => ({
  categories: [],
  collection: {},
  currentPage: 1,
  locale: 'en-US',
  posts: [],
  searchFilters: [],
  searchQuery: '',
  subdirectory: '',
  totalPostCount: 0,
  translations: {},
});

export default defineStore('globalStore', {
  state: () => ({ ...defaultState() }),
  getters: {
    getCategories: (state) => state.categories,
    getCollection: (state) => state.collection,
    getCurrentPage: (state) => state.currentPage,
    getFilteredPosts: (state) => {
      const getPostCategoryIds = (post) => (post?.fields?.categories ? post?.fields?.categories.map((c) => c.sys.id) : []);
      let filteredPosts = state.posts;

      if (state.searchQuery) {
        filteredPosts = filteredPosts.filter((post) => post?.fields?.title.toLowerCase().includes(state.searchQuery.toLowerCase()));
      }

      if (state.searchFilters.length > 0) {
        if (state.collection?.content?.searchLogicOperator === 'And') {
          filteredPosts = filteredPosts.filter((post) => state.searchFilters.every((filter) => getPostCategoryIds(post).includes(filter.value)));
        } else if (state.collection?.content?.searchLogicOperator === 'Or') {
          filteredPosts = filteredPosts.filter((post) => state.searchFilters.some((filter) => getPostCategoryIds(post).includes(filter.value)));
        } else if (state.collection?.content?.searchLogicOperator === 'Inter-filter And Intra-filter Or') {
          const filterGroups = state.searchFilters.reduce((groups, filter) => {
            // eslint-disable-next-line no-param-reassign
            (groups[filter.parentId] ??= []).push(filter.value);
            return groups;
          }, {});
          filteredPosts = filteredPosts.filter((post) => {
            const postFilterIds = getPostCategoryIds(post);

            return Object.values(filterGroups).every((groupValues) => groupValues.some((value) => postFilterIds.includes(value)));
          });
        }
      }

      return filteredPosts;
    },
    getLocale: (state) => state.locale,
    getPosts: (state) => state.posts,
    getResultCountText: (state) => {
      const postCount = state.getSearchFilters.length || state.searchQuery ? state.getFilteredPosts.length : state.totalPostCount;
      const substitutionString = '{{count}}';
      if (Array.isArray(state.getFilteredPosts) && state.getFilteredPosts.length === 1) {
        return state.translations.ITFE_MEDIA_POST_COUNT_SINGULAR_TEXT.replace(substitutionString, 1);
      }
      return state.translations.ITFE_MEDIA_POST_COUNT_PLURAL_TEXT.replace(substitutionString, postCount);
    },
    getSearchFilters: (state) => state.searchFilters,
    getSearchQuery: (state) => state.searchQuery,
    getSubdirectory: (state) => state.subdirectory,
    getTranslations: (state) => state.translations,
  },
  actions: {
    addSearchFilter(payload) {
      this.searchFilters.push(payload);
    },
    async fetchPosts(postOffsetPage = 1) {
      try {
        const maxPages = Math.ceil(this.totalPostCount / 500);
        const url = `${window.location.origin}/m/api/getPosts/?page=${postOffsetPage}&collectionId=${this.collection?.id}&locale=${this.locale}`;

        const response = await request({
          method: 'GET',
          url,
        });
        this.appendPosts(response);

        if (
          postOffsetPage + 1 <= maxPages
          && response
          && response.length > 0
          && this.posts.length < this.totalPostCount
        ) this.fetchPosts(postOffsetPage + 1);
      } catch (error) {
        throw new Error('Error calling getPosts API', error?.message);
      }
    },
    removeSearchFilter(payload) {
      this.searchFilters = this.searchFilters.filter((filter) => filter.value !== payload);
    },
    resetCurrentPage() {
      this.currentPage = defaultState().currentPage;
    },
    setCategories(payload) {
      const sortCategoriesAlphabetically = (categories) => categories.sort((a, b) => {
        if (a?.fields?.title < b?.fields?.title) return -1;
        return 1;
      });

      const sortCategoriesReverseAlphabetically = (categories) => categories.sort((a, b) => {
        if (a?.fields?.title > b?.fields?.title) return -1;
        return 1;
      });

      const sortChildren = (category) => {
        if (Array.isArray(category?.fields?.children) && category.fields.children.length > 0) {
          if (category?.fields?.childrenSort === 'Alphabetical') {
            sortCategoriesAlphabetically(category.fields.children);
          } else if (category?.fields?.childrenSort === 'Reverse Alphabetical') {
            sortCategoriesReverseAlphabetically(category.fields.children);
          }

          sortChildren(category.fields.children);
        }
      };

      if (Array.isArray(payload)) {
        if (this.collection?.content?.topLevelCategorySortDirection === 'Alphabetical') {
          this.categories = sortCategoriesAlphabetically(payload);
        } else if (this.collection?.content?.topLevelCategorySortDirection === 'Reverse Alphabetical') {
          this.categories = sortCategoriesReverseAlphabetically(payload);
        }
        this.categories = payload;

        this.categories.forEach((i) => {
          sortChildren(i);
        });
      } else {
        this.categories = [];
      }
    },
    setCollection(payload) {
      this.collection = payload;
    },
    setCurrentPage(payload) {
      this.currentPage = payload;
    },
    setInitialState() {
      const {
        categories = defaultState().categories,
        collection = defaultState().collection,
        locale = defaultState().locale,
        posts = defaultState().posts,
        searchQuery = defaultState().searchQuery,
        searchFilters = defaultState().searchFilters,
        totalPostCount = defaultState().totalPostCount,
        translations = defaultState().translations,
        subdirectory = defaultState().subdirectory,
      } = window.appData || {};

      // Set first
      this.setCollection(collection);

      // Everything else
      this.setCategories(categories);
      this.setLocale(locale);
      this.setPosts(posts);
      this.setSearchFilters(searchFilters);
      this.setSearchQuery(searchQuery);
      this.setTotalPostCount(totalPostCount);
      this.setTranslations(translations);
      this.setSubdirectory(subdirectory);
    },
    setLocale(payload) {
      this.locale = payload;
    },
    appendPosts(payload) {
      this.posts = postSort(this.posts.concat(payload), this.collection?.content?.postSortDirection);
    },
    setPosts(payload) {
      this.posts = postSort(payload, this.collection?.content?.postSortDirection);
    },
    setSearchFilters(payload) {
      this.searchFilters = payload;
    },
    setSearchQuery(payload) {
      this.searchQuery = payload;
    },
    setSubdirectory(payload) {
      this.subdirectory = payload;
    },
    setTotalPostCount(payload) {
      this.totalPostCount = payload;
    },
    setTranslations(payload) {
      const defaultTranslations = {
        ITFE_MEDIA_FILTER_PLACEHOLDER_TEXT: 'Select Option',
        ITFE_MEDIA_POST_COUNT_PLURAL_TEXT: '{{count}} results found.',
        ITFE_MEDIA_POST_COUNT_SINGULAR_TEXT: '{{count}} result found.',
      };

      const translations = {};

      Object.keys(defaultTranslations).forEach((i) => {
        if (!payload[i]) {
          // eslint-disable-next-line no-console
          console.log(`${i} does not exist in the application configuration and the default translation has been used.`);
          translations[i] = defaultTranslations[i];
        } else {
          translations[i] = payload[i];
        }
      });

      this.translations = translations;
    },
  },
});
